/* Экран 1b — главная: сплит-герой, бегущая строка, каталог, отзывы. */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { imageUrl } from '../lib/api.js';
import { useStore } from '../store/StoreContext.jsx';
import { useToast } from '../store/ToastContext.jsx';
import { useUI } from '../store/UIContext.jsx';
import { ProductCard, ProductSkeleton, ReviewCard } from '../components/Product.jsx';
import { Search, Sort } from '../components/CatalogTools.jsx';
import Button from '../components/Button.jsx';
import AllReviewsModal from '../components/AllReviewsModal.jsx';
import { goodsLabel, ratingsLabel } from '../lib/format.js';
import {
  useAsyncButton, useCountUp, useFlip, useParallax, useReveal, useSplitLines, scrollToEl,
} from '../hooks/useMotion.js';
import { useTitle } from '../hooks/useTitle.js';

const MARQUEE = 'НОВЫЙ ДРОП 2026 • SAINTS • НАПАЛЬЧНИКИ PRO • ДОСТАВКА СДЭК ПО РФ И СНГ • ';
const PAGE_SIZE = 9;

export default function Home() {
  const { catalog, addToCart } = useStore();
  const toast = useToast();
  const ui = useUI();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const category = params.get('category') || 'all';
  const sort = params.get('sort') || 'default';
  const [search, setSearch] = useState(params.get('search') || '');
  const [query, setQuery] = useState(params.get('search') || '');

  const [items, setItems] = useState(() => catalog.products.slice(0, PAGE_SIZE));
  const [total, setTotal] = useState(catalog.products.length);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [moreState, loadMore] = useAsyncButton();

  const catalogRef = useRef(null);
  const heroRef = useParallax(0.09);
  const titleRef = useSplitLines();
  const pageRef = useReveal([items.length, catalog.categories.length]);
  const gridRef = useFlip(items.map((p) => p.slug).join());
  const countRef = useCountUp(catalog.stats.products, { duration: 900 });
  const requestId = useRef(0);
  const firstRender = useRef(true);

  useTitle(null);

  // Поиск не дёргает сервер на каждую букву
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 320);
    return () => clearTimeout(id);
  }, [search]);

  // Адресная строка держит состояние каталога — ссылкой можно поделиться
  useEffect(() => {
    const next = {};
    if (category !== 'all') next.category = category;
    if (sort !== 'default') next.sort = sort;
    if (query) next.search = query;
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const load = useCallback(
    async (nextPage) => {
      const mine = ++requestId.current;
      const res = await api.products({ category, search: query, sort, page: nextPage, pageSize: PAGE_SIZE });
      if (mine !== requestId.current) return null;

      setTotal(res.total);
      setPage(res.page);
      setItems((prev) => (nextPage > 1 ? [...prev, ...res.items] : res.items));
      return res;
    },
    [category, query, sort],
  );

  useEffect(() => {
    // Первый экран уже пришёл в bootstrap — лишний запрос не нужен
    if (firstRender.current && category === 'all' && sort === 'default' && !query && catalog.products.length) {
      firstRender.current = false;
      setItems(catalog.products.slice(0, PAGE_SIZE));
      setTotal(catalog.products.length);
      return;
    }
    firstRender.current = false;

    setLoading(true);
    load(1)
      .catch(() => toast.error('Не удалось загрузить каталог'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort, query, catalog.products]);

  const handleAdd = useCallback(
    async (product, sourceEl) => {
      // Товару с размерами нужен выбор — уводим на карточку
      if (product.variants.length > 1) {
        navigate(`/product/${product.slug}`);
        return;
      }
      try {
        await addToCart({ slug: product.slug, sourceEl });
      } catch (err) {
        toast.error(err.message || 'Не удалось добавить товар');
      }
    },
    [addToCart, navigate, toast],
  );

  const setParam = (key, value) => {
    const next = Object.fromEntries(params);
    if (value && value !== 'all' && value !== 'default') next[key] = value;
    else delete next[key];
    setParams(next, { replace: true });
  };

  const chips = [{ slug: 'all', title: 'Все' }, ...catalog.categories];
  const filtered = category !== 'all' || !!query || sort !== 'default';

  return (
    <div className="page page--home" ref={pageRef}>
      <section className="hero">
        <div className="hero__inner">
          <span className="hero__eyebrow" data-reveal="sm">SAINTS STORE</span>

          <h1 className="hero__title" ref={titleRef}>
            <span className="split-line" style={{ '--d': '0ms' }}><i>МЕРЧ</i></span>
            <span className="split-line" style={{ '--d': '110ms' }}><i>КОМАНДЫ</i></span>
            <span className="split-line" style={{ '--d': '220ms' }}><i><em>SAINTS</em></i></span>
          </h1>

          <p className="hero__text" data-reveal="sm" style={{ '--reveal-delay': '340ms' }}>
            Игровые напальчники, джерси, худи и футболки на каждый день — от команды, за которую
            вы болеете.
          </p>

          <div className="hero__cta" data-reveal="sm" style={{ '--reveal-delay': '440ms' }}>
            <Button variant="accent" size="lg" onClick={() => scrollToEl(catalogRef.current, 20)}>
              В каталог
            </Button>
            <Button variant="ghostDark" size="lg" onClick={ui.openBrand}>
              О бренде
            </Button>
          </div>

          <div className="hero__stats" data-reveal="sm" style={{ '--reveal-delay': '540ms' }}>
            <div className="hero__stat"><b>2–4 дня</b><span>доставка СДЭК</span></div>
            <div className="hero__stat"><b ref={countRef}>0</b><span>позиций в новом дропе</span></div>
            <div className="hero__stat">
              <b>{catalog.stats.rating}</b>
              <span>{ratingsLabel(catalog.stats.reviews)}</span>
            </div>
          </div>
        </div>

        <div className="hero__media">
          <div className="hero__media-inner" ref={heroRef}>
            <img src={imageUrl('/uploads/jersey.jpg')} alt="Джерси Saints 2026" fetchPriority="high" />
          </div>
          <div className="hero__fade" />
          <div className="hero__scroll"><i />ЛИСТАЙТЕ ВНИЗ</div>
        </div>
      </section>

      <div className="marquee" aria-hidden="true">
        <div className="marquee__track">{MARQUEE.repeat(4)}{MARQUEE.repeat(4)}</div>
      </div>

      <section className="section section--light" ref={catalogRef}>
        <div className="section__inner">
          <div className="section-head">
            <h2 className="section-title" data-reveal="left">Новый дроп</h2>
            <Chips items={chips} active={category} onPick={(slug) => setParam('category', slug)} />
          </div>

          <div className="tools" style={{ marginTop: 20 }} data-reveal="sm">
            <Search value={search} onChange={setSearch} />
            <Sort value={sort} onChange={(v) => setParam('sort', v)} />
          </div>

          {filtered ? (
            <div className="catalog__meta">
              <span>
                {loading
                  ? 'Ищем…'
                  : (
                    <>
                      Нашлось <mark>{goodsLabel(total)}</mark>
                      {query ? <> по запросу «{query}»</> : null}
                    </>
                  )}
              </span>
              <button
                type="button"
                className="link-underline"
                onClick={() => {
                  setSearch('');
                  setParams({}, { replace: true });
                }}
              >
                Сбросить фильтры
              </button>
            </div>
          ) : null}

          <div className="catalog__grid" data-stagger="80" ref={gridRef}>
            {loading && !items.length
              ? Array.from({ length: 6 }, (_, i) => <ProductSkeleton key={i} />)
              : items.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} onAdd={handleAdd} />
                ))}
            {!loading && !items.length ? (
              <p className="catalog__empty">
                {query
                  ? `По запросу «${query}» ничего не нашлось.`
                  : 'В этой категории пока пусто — но дроп ещё не закончился.'}
              </p>
            ) : null}
          </div>

          {items.length < total ? (
            <div className="catalog__more">
              <Button
                variant="ghost"
                state={moreState === 'done' ? 'idle' : moreState}
                onClick={() =>
                  loadMore(() => load(page + 1), { doneMs: 0 }).catch(() =>
                    toast.error('Не удалось загрузить ещё'),
                  )
                }
              >
                Показать ещё {total - items.length} из {total}
              </Button>
            </div>
          ) : null}
        </div>
      </section>

      <section className="section section--dark">
        <div className="section__inner">
          <div className="section-head">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
              <h2 className="section-title" data-reveal="left">Отзывы</h2>
              <span style={{ fontSize: 15, color: 'var(--text-on-dark-3)' }} data-reveal="sm">
                <span className="u-accent">★</span> {catalog.stats.rating} — больше{' '}
                {Math.floor(catalog.stats.reviews / 100) * 100} оценок
              </span>
            </div>
            <Button
              variant="ghostDark"
              className="btn--reviews"
              data-reveal="right"
              onClick={() => setShowReviews(true)}
            >
              Все отзывы
            </Button>
          </div>

          <div className="grid-3" style={{ marginTop: 32 }} data-stagger="110">
            {catalog.homeReviews.map((r) => (
              <ReviewCard key={r.id} review={r} dark showProduct />
            ))}
          </div>
        </div>
      </section>

      {showReviews ? <AllReviewsModal onClose={() => setShowReviews(false)} /> : null}
    </div>
  );
}

/* ─────────────────  Фильтры с переезжающей заливкой  ───────────── */

function Chips({ items, active, onPick }) {
  const wrapRef = useRef(null);
  const inkRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const ink = inkRef.current;
    if (!wrap || !ink) return;

    const move = () => {
      const chip = wrap.querySelector('.chip.is-active');
      if (!chip) {
        ink.style.width = '0';
        return;
      }
      const base = wrap.getBoundingClientRect();
      const r = chip.getBoundingClientRect();
      ink.style.width = `${r.width}px`;
      ink.style.transform = `translateX(${r.left - base.left}px)`;
    };
    move();
    window.addEventListener('resize', move);
    return () => window.removeEventListener('resize', move);
  }, [active, items.length]);

  return (
    <div className="chips" ref={wrapRef} data-reveal="right">
      <span className="chips__ink" ref={inkRef} />
      {items.map((c) => (
        <button
          key={c.slug}
          type="button"
          className={`chip${c.slug === active ? ' is-active' : ''}`}
          onClick={() => onPick(c.slug)}
        >
          {c.title}
        </button>
      ))}
    </div>
  );
}
